import { z } from 'zod';
import { ROLES } from './roles.js';
import { DELIVERY_STATUSES, TRANSACTION_STATUSES } from './numbering.js';
import { PROCESSING_STAGES } from './processing.js';
import { EXCEPTION_CATEGORIES, EXCEPTION_SEVERITIES } from './exceptions.js';
import { PAYMENT_FLAGS, PAYMENT_STATUSES } from './payments.js';
import {
  RECONCILIATION_ACTUAL_FIELDS,
  RECONCILIATION_STATUSES,
  type ReconciliationActuals,
} from './reconciliation.js';
import { HANDOVER_STATUSES } from './handover.js';
import {
  CONTROL_REVIEW_CATEGORIES,
  CONTROL_REVIEW_CATEGORY_SOURCES,
  CONTROL_REVIEW_PRIORITIES,
  CONTROL_REVIEW_STATUSES,
} from './control-review.js';

/** Lowercased login identifier: letters, digits, dot, dash, underscore. */
export const usernameSchema = z
  .string()
  .trim()
  .min(3, 'Username must be at least 3 characters')
  .max(64)
  .regex(/^[A-Za-z0-9._-]+$/, 'Username contains invalid characters')
  .transform((v) => v.toLowerCase());

export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128);

export const loginSchema = z.object({
  username: z
    .string()
    .trim()
    .min(1)
    .max(64)
    .transform((v) => v.toLowerCase()),
  password: z.string().min(1).max(128),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const createUserSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(120),
    username: usernameSchema,
    password: passwordSchema,
    role: z.enum(ROLES),
    branchId: z.string().trim().min(1).nullish(),
  })
  .superRefine((val, ctx) => {
    if (val.role === 'ADMIN') {
      if (val.branchId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['branchId'],
          message: 'Admin accounts are cross-branch and must not be assigned to a branch',
        });
      }
    } else if (!val.branchId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['branchId'],
        message: 'Employee accounts must be assigned to a branch',
      });
    }
  });
export type CreateUserInput = z.infer<typeof createUserSchema>;

export const updateUserSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    role: z.enum(ROLES).optional(),
    branchId: z.string().trim().min(1).nullable().optional(),
    isActive: z.boolean().optional(),
    password: passwordSchema.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'No changes supplied' });
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

export const listUsersQuerySchema = z.object({
  /** Branch filter. Omitted/ALL = no filter (admin) */
  branch: z.string().trim().min(1).optional(),
  role: z.enum(ROLES).optional(),
  includeInactive: z
    .union([z.literal('true'), z.literal('false'), z.boolean()])
    .optional()
    .transform((v) => v === true || v === 'true'),
  q: z.string().trim().max(64).optional(),
});
export type ListUsersQuery = z.infer<typeof listUsersQuerySchema>;

export const listAuditQuerySchema = z.object({
  branch: z.string().trim().min(1).optional(),
  action: z.string().trim().max(64).optional(),
  entityType: z.string().trim().max(64).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListAuditQuery = z.infer<typeof listAuditQuerySchema>;

/* ============================================================
 * Phase 2A - services/pricing + receiving (transaction creation)
 * ============================================================ */

export const SERVICE_KINDS = ['MAIN', 'ADDON'] as const;
export type ServiceKind = (typeof SERVICE_KINDS)[number];

export const SERVICE_UNITS = ['KG', 'PIECE', 'FLAT'] as const;
export type ServiceUnitName = (typeof SERVICE_UNITS)[number];

export const PAYMENT_METHODS = ['CASH', 'GCASH', 'MAYA', 'OTHER'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const FULFILLMENT_METHODS = ['PICKUP', 'DELIVERY'] as const;
export type FulfillmentMethod = (typeof FULFILLMENT_METHODS)[number];

/** 0 - 100,000.00 pesos in integer centavos (never floats). */
const centsSchema = z.number().int().min(0).max(10_000_000);

export const createServiceSchema = z
  .object({
    branchId: z.string().trim().min(1),
    name: z.string().trim().min(1, 'Name is required').max(80),
    kind: z.enum(SERVICE_KINDS).default('MAIN'),
    unit: z.enum(SERVICE_UNITS),
    priceCents: centsSchema,
    /** Minimum load in whole kilograms for a flat-rate primary service. */
    minKilos: z.number().int().min(1).max(1000).nullish(),
    isActive: z.boolean().optional(),
  })
  .superRefine((val, ctx) => {
    // Add-ons are per-quantity flat lines (price x qty); the priced
    // basis of the MAIN service is what supports KG/PIECE.
    if (val.kind === 'ADDON' && val.unit !== 'FLAT') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['unit'],
        message: 'Additional services are charged as a flat amount per quantity',
      });
    }
    if (val.kind === 'ADDON' && val.minKilos != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['minKilos'],
        message: 'Additional services cannot have a minimum load',
      });
    } else if (val.minKilos != null && val.unit !== 'FLAT') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['minKilos'],
        message: 'Minimum-load pricing requires a flat rate',
      });
    }
  });
export type CreateServiceInput = z.infer<typeof createServiceSchema>;

export const updateServiceSchema = z
  .object({
    name: z.string().trim().min(1).max(80).optional(),
    kind: z.enum(SERVICE_KINDS).optional(),
    unit: z.enum(SERVICE_UNITS).optional(),
    priceCents: centsSchema.optional(),
    /** null clears the minimum; omit to leave it unchanged. */
    minKilos: z.number().int().min(1).max(1000).nullable().optional(),
    isActive: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'No changes supplied' });
export type UpdateServiceInput = z.infer<typeof updateServiceSchema>;

export const listServicesQuerySchema = z.object({
  branch: z.string().trim().min(1).optional(),
  includeInactive: z
    .union([z.literal('true'), z.literal('false'), z.boolean()])
    .optional()
    .transform((v) => v === true || v === 'true'),
  kind: z.enum(SERVICE_KINDS).optional(),
});
export type ListServicesQuery = z.infer<typeof listServicesQuerySchema>;

export const transactionItemInputSchema = z.object({
  description: z.string().trim().min(1).max(80),
  quantity: z.number().int().min(1).max(10_000),
});

export const createTransactionSchema = z
  .object({
    branchId: z.string().trim().min(1),
    customerName: z.string().trim().min(1, 'Customer name is required').max(120),
    customerPhone: z
      .string()
      .trim()
      .regex(/^[0-9+()\-\s]{7,20}$/, 'Enter a valid contact number')
      .optional()
      .or(z.literal('').transform(() => undefined)),
    serviceId: z.string().trim().min(1),
    /** Required: total expected items (accountability baseline). */
    itemCount: z.number().int().min(1).max(10_000),
    /** Grams; required when the service is priced per kilo. */
    weightGrams: z.number().int().min(100).max(1_000_000).optional(),
    /** Optional breakdown; when present its quantities must sum to itemCount. */
    items: z.array(transactionItemInputSchema).min(1).max(50).optional(),
    /** Additional services (flat price x quantity), snapshotted at creation. */
    addons: z
      .array(z.object({ serviceId: z.string().trim().min(1), quantity: z.number().int().min(1).max(999) }))
      .max(20)
      .optional(),
    paymentMethod: z.enum(PAYMENT_METHODS).optional(),
    amountPaidCents: centsSchema.optional(),
    fulfillmentMethod: z.enum(FULFILLMENT_METHODS).default('PICKUP'),
    deliveryAddress: z.string().trim().max(200).optional(),
    /** ISO datetime - expected completion. */
    expectedAt: z.string().datetime().optional(),
    notes: z.string().trim().max(500).optional(),
  })
  .superRefine((val, ctx) => {
    if (val.items && val.items.length > 0) {
      const sum = val.items.reduce((total, item) => total + item.quantity, 0);
      if (sum !== val.itemCount) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['items'],
          message: `Item breakdown sums to ${sum} but total item count is ${val.itemCount}`,
        });
      }
    }
    if (val.fulfillmentMethod === 'DELIVERY' && !val.deliveryAddress) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['deliveryAddress'],
        message: 'Delivery address is required for delivery',
      });
    }
  });
export type CreateTransactionInput = z.infer<typeof createTransactionSchema>;

/** Phase 2B - controlled status transition input. */
export const updateTransactionStatusSchema = z.object({
  status: z.enum(TRANSACTION_STATUSES),
  /** Optional actor note stored on the history entry and audit metadata. */
  note: z.string().trim().max(300).optional(),
});
export type UpdateTransactionStatusInput = z.infer<typeof updateTransactionStatusSchema>;

export const listTransactionsQuerySchema = z.object({
  branch: z.string().trim().min(1).optional(),
  status: z.enum(TRANSACTION_STATUSES).optional(),
  /** Phase 2D - narrow the queue to one fulfillment method (pickup/delivery). */
  fulfillment: z.enum(FULFILLMENT_METHODS).optional(),
  q: z.string().trim().max(64).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListTransactionsQuery = z.infer<typeof listTransactionsQuerySchema>;

/* ============================================================
 * Phase 2C - laundry processing workflow (item accountability)
 * ============================================================ */

/** Open a processing stage (start time is stamped server-side). */
export const startProcessingSchema = z.object({
  stage: z.enum(PROCESSING_STAGES),
  /** Resource identifier (W1..W3, D1..D2) - required for machine stages. */
  machine: z.string().trim().min(1).max(10).optional(),
});
export type StartProcessingInput = z.infer<typeof startProcessingSchema>;

/** Finish the open stage with the actual counted quantity. */
export const completeProcessingSchema = z.object({
  actualCount: z.number().int().min(0).max(10_000),
  note: z.string().trim().max(300).optional(),
});
export type CompleteProcessingInput = z.infer<typeof completeProcessingSchema>;

/** Employee-area queue: washing (PENDING+WASHING) or folding (FOLDING). */
export const processingQueueQuerySchema = z.object({
  area: z.enum(['washing', 'folding']),
  branch: z.string().trim().min(1).optional(),
});
export type ProcessingQueueQuery = z.infer<typeof processingQueueQuerySchema>;

// Phase 2F - delivery. The status list lives in numbering.ts (single source of
// truth, re-exported through index.ts); schemas only wrap it for validation.
export const deliveryStatusSchema = z.enum(DELIVERY_STATUSES);

/**
 * Project identifier format: Prisma cuid() - 25 chars, leading 'c',
 * lowercase alphanumeric (e.g. cmuux5ohd0000ueec6pa591ou).
 * Deliberately NOT uuid: this system never generates UUIDs.
 */
export const CUID_REGEX = /^c[0-9a-z]{24}$/;
export const idSchema = z.string().regex(CUID_REGEX, 'Invalid identifier');

export const deliveryHandoffCreateSchema = z.object({
  transactionId: idSchema,
  deliveryMethod: z.enum(['DRIVER', 'OTHER']),
  actorId: idSchema,
  notes: z.string().max(1000).optional(),
  itemCountAtHandoff: z.number().int().positive().optional(),
  /** Delivery handoff is written server-side; client never supplies timestamp. */
  expectedHandoffAt: z.string().datetime().optional(),
});

/** One delivery status transition request (server re-validates every edge). */
export const deliveryTransitionSchema = z.object({
  to: deliveryStatusSchema,
  /** Free-text handler name captured when the admin assigns the delivery. */
  assignee: z.string().trim().min(1).max(120).optional(),
  /** Required on OUT_FOR_DELIVERY: how the laundry physically leaves. */
  deliveryMethod: z.enum(['DRIVER', 'OTHER']).optional(),
  /** Optional handoff count snapshot; defaults to the transaction itemCount. */
  itemCountAtHandoff: z.number().int().min(1).max(10_000).optional(),
  notes: z.string().trim().max(1000).optional(),
});
export type DeliveryTransitionInput = z.infer<typeof deliveryTransitionSchema>;

export const listDeliveryQuerySchema = z.object({
  branch: z.string().trim().min(1).optional(),
  q: z.string().trim().max(64).optional(),
  deliveryStatus: deliveryStatusSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListDeliveryQuery = z.infer<typeof listDeliveryQuerySchema>;

/* ============================================================
 * Phase 2G.2 - transaction aging & operational exceptions
 * ============================================================ */

/**
 * GET /api/exceptions query. Admin-only (detected server-side via the
 * monitoring.view capability); `branch` is a VIEW FILTER resolved through
 * resolveBranchScope just like every other list endpoint.
 *
 * `category` narrows to one detection rule, `severity` to one severity
 * band, and `status` narrows to exceptions whose UNDERLYING transaction is
 * currently in that workflow status (e.g. status=READY shows only
 * ready-queue observations).
 */
export const listExceptionsQuerySchema = z.object({
  branch: z.string().trim().min(1).optional(),
  category: z.enum(EXCEPTION_CATEGORIES).optional(),
  severity: z.enum(EXCEPTION_SEVERITIES).optional(),
  status: z.enum(TRANSACTION_STATUSES).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListExceptionsQuery = z.infer<typeof listExceptionsQuerySchema>;

/* ============================================================
 * Phase 2G.3 - employee accountability & operational performance
 * ============================================================ */

/** Manila calendar date (YYYY-MM-DD), e.g. "2026-10-07". */
const manilaDateSchema = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected a YYYY-MM-DD date')
  .refine((v) => !Number.isNaN(Date.parse(`${v}T00:00:00+08:00`)), 'Not a real calendar date');

/**
 * Shared filters for every accountability endpoint (admin-only, resolved
 * server-side). `branch` is a VIEW filter via resolveBranchScope, `date`
 * is an Asia/Manila business-date shortcut (TODAY), and `from`/`to` are
 * optional Manila calendar dates - deliberately basic, never a reporting
 * engine. Browser-local time is never authoritative.
 */
const accountabilityFilterFields = {
  branch: z.string().trim().min(1).optional(),
  employee: z.string().trim().min(1).optional(),
  role: z.enum(ROLES).optional(),
  date: z.enum(['TODAY']).optional(),
  from: manilaDateSchema.optional(),
  to: manilaDateSchema.optional(),
};

const accountabilityDateRules = (
  val: { date?: 'TODAY'; from?: string; to?: string },
  ctx: z.RefinementCtx,
): void => {
  if (val.date && (val.from || val.to)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['date'],
      message: 'date=TODAY cannot be combined with from/to',
    });
  }
  if (val.from && val.to && val.from > val.to) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['from'],
      message: 'from must not be after to',
    });
  }
};

/**
 * GET /api/accountability - unified accountability event feed.
 * `transaction` narrows to one transaction (id or number). `offset` is
 * capped because pages are merged in memory from bounded per-source
 * scans (deterministic: occurredAt desc, id desc).
 */
export const listAccountabilityQuerySchema = z
  .object({
    ...accountabilityFilterFields,
    transaction: z.string().trim().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).max(10_000).default(0),
  })
  .superRefine(accountabilityDateRules);
export type ListAccountabilityQuery = z.infer<typeof listAccountabilityQuerySchema>;

/** GET /api/accountability/summary - per-employee operational counts. */
export const accountabilitySummaryQuerySchema = z
  .object({
    ...accountabilityFilterFields,
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).max(10_000).default(0),
  })
  .superRefine(accountabilityDateRules);
export type AccountabilitySummaryQuery = z.infer<typeof accountabilitySummaryQuerySchema>;

/** GET /api/accountability/employees/:id - read-only employee detail. */
export const accountabilityEmployeeQuerySchema = z
  .object({
    branch: z.string().trim().min(1).optional(),
    date: z.enum(['TODAY']).optional(),
    from: manilaDateSchema.optional(),
    to: manilaDateSchema.optional(),
  })
  .superRefine(accountabilityDateRules);
export type AccountabilityEmployeeQuery = z.infer<typeof accountabilityEmployeeQuerySchema>;

/** GET /api/accountability/transactions/:ref - stage timeline (branch view filter only). */
export const accountabilityTimelineQuerySchema = z.object({
  branch: z.string().trim().min(1).optional(),
});
export type AccountabilityTimelineQuery = z.infer<typeof accountabilityTimelineQuerySchema>;

/* ============================================================
 * Phase 2G.4 - branch operational & business comparison
 * ============================================================ */

/**
 * GET /api/branch-comparison query. Admin-only (monitoring.view, enforced
 * server-side); `branch` is a VIEW filter resolved through
 * resolveBranchScope, `date`/`from`/`to` reuse the exact Asia/Manila
 * business-date rules of Phase 2G.3 (from inclusive, to inclusive at the
 * business-date level; browser-local time is never authoritative).
 *
 * Omitting every date field means "all recorded history" - the same
 * unbounded semantics the accountability feed uses.
 */
export const branchComparisonQuerySchema = z
  .object({
    branch: z.string().trim().min(1).optional(),
    date: z.enum(['TODAY']).optional(),
    from: manilaDateSchema.optional(),
    to: manilaDateSchema.optional(),
  })
  .superRefine(accountabilityDateRules);
export type BranchComparisonQuery = z.infer<typeof branchComparisonQuerySchema>;

/* ============================================================
 * Phase 2G.5 - discrepancy investigation & resolution visibility
 * ============================================================ */

/**
 * GET /api/discrepancies query. Admin-only (monitoring.view, enforced
 * server-side); `branch` is a VIEW filter resolved through
 * resolveBranchScope, and the Manila date fields reuse the Phase 2G.3
 * business-date rules (date=TODAY cannot combine with from/to, from <=
 * to). The date range narrows to the day the count was RECORDED - the
 * server-stamped completion time of the step, in Asia/Manila; browser
 * time is never authoritative.
 *
 * `transaction` searches the transaction reference (exact id or a
 * case-insensitive substring of the branch-scoped number), `stage` narrows
 * to one processing stage, and `employee` narrows to the recorded actor -
 * validated against the server-side scope, never trusted from the client.
 *
 * `discrepancyOnly` (default true) keeps only completed steps whose
 * recorded difference is non-zero. Setting it to `false` also shows
 * zero-difference completion records (counts that matched), which are
 * relevant investigation context and are never discarded server-side.
 * The route converts the validated 'true'/'false' string to a boolean.
 */
export const listDiscrepanciesQuerySchema = z
  .object({
    branch: z.string().trim().min(1).optional(),
    employee: z.string().trim().min(1).optional(),
    stage: z.enum(PROCESSING_STAGES).optional(),
    transaction: z.string().trim().min(1).max(64).optional(),
    discrepancyOnly: z.enum(['true', 'false']).default('true'),
    date: z.enum(['TODAY']).optional(),
    from: manilaDateSchema.optional(),
    to: manilaDateSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).max(10_000).default(0),
  })
  .superRefine(accountabilityDateRules);
export type ListDiscrepanciesQuery = z.infer<typeof listDiscrepanciesQuerySchema>;

/** GET /api/discrepancies/transactions/:ref - transaction-level view (branch view filter only). */
export const discrepancyTransactionQuerySchema = z.object({
  branch: z.string().trim().min(1).optional(),
});
export type DiscrepancyTransactionQuery = z.infer<typeof discrepancyTransactionQuerySchema>;

/* ============================================================
 * Phase 2G.6 - payment tracking & cash accountability
 * ============================================================ */

/**
 * GET /api/payments query - admin-only payment & outstanding-balance
 * monitoring (monitoring.view, enforced server-side). `branch` is a VIEW
 * filter resolved through resolveBranchScope (never trusted from the
 * client); `status` and `flag` narrow server-side using the shared
 * vocabulary in payments.ts; `q` searches the transaction reference (exact
 * id or case-insensitive substring of the number or customer name).
 *
 * The Manila date fields reuse the Phase 2G.3 business-date rules and
 * narrow to the day the PAYMENT WAS RECORDED - in this model that is the
 * receiving moment (the server-stamped transaction createdAt), the only
 * payment timestamp the data has. Browser time is never authoritative.
 */
export const paymentsListQuerySchema = z
  .object({
    branch: z.string().trim().min(1).optional(),
    status: z.enum(PAYMENT_STATUSES).optional(),
    flag: z.enum(PAYMENT_FLAGS).optional(),
    q: z.string().trim().min(1).max(64).optional(),
    date: z.enum(['TODAY']).optional(),
    from: manilaDateSchema.optional(),
    to: manilaDateSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).max(10_000).default(0),
  })
  .superRefine(accountabilityDateRules);
export type PaymentsListQuery = z.infer<typeof paymentsListQuerySchema>;

/**
 * Period presets for financial dashboard filters.
 */
export const FINANCIAL_PERIODS = ['daily', 'weekly', 'monthly', 'yearly', 'custom'] as const;
export type FinancialPeriod = (typeof FINANCIAL_PERIODS)[number];

/**
 * GET /api/payments/collections query - collection reconciliation by
 * branch/method over the payment-recorded window (same Manila rules and
 * same single-count basis as the monitoring list, so the two reconcile).
 */
export const paymentsCollectionsQuerySchema = z
  .object({
    branch: z.string().trim().min(1).optional(),
    period: z.enum(FINANCIAL_PERIODS).optional(),
    date: z.enum(['TODAY']).optional(),
    from: manilaDateSchema.optional(),
    to: manilaDateSchema.optional(),
  })
  .superRefine(accountabilityDateRules);
export type PaymentsCollectionsQuery = z.infer<typeof paymentsCollectionsQuerySchema>;

/** GET /api/payments/transactions/:ref - payment history (branch view filter only). */
export const paymentHistoryQuerySchema = z.object({
  branch: z.string().trim().min(1).optional(),
});
export type PaymentHistoryQuery = z.infer<typeof paymentHistoryQuerySchema>;

/* ============================================================
 * Phase 2G.7 - daily cash reconciliation & handover accountability
 * ============================================================ */

/** Non-negative integer-cent amount (mirrors the finance bounds). */
const actualCentsField = z.number().int().min(0).max(100_000_000);

/** The four actual-count fields: provided (>=0 cents) or null (not counted). */
const actualsFields = {
  cashCountedCents: actualCentsField.nullable().optional(),
  gcashVerifiedCents: actualCentsField.nullable().optional(),
  mayaVerifiedCents: actualCentsField.nullable().optional(),
  otherActualCents: actualCentsField.nullable().optional(),
};

/**
 * GET /api/reconciliations query - admin-only reconciliation list.
 * `branch` is a VIEW filter via resolveBranchScope; `status` narrows the
 * lifecycle state; the Manila `date`/`from`/`to` fields filter the BUSINESS
 * DATE the reconciliation covers (not when the row was created); `q`
 * matches a preparer/submitter/reviewer name substring.
 */
export const reconciliationListQuerySchema = z
  .object({
    branch: z.string().trim().min(1).optional(),
    status: z.enum(RECONCILIATION_STATUSES).optional(),
    q: z.string().trim().min(1).max(64).optional(),
    date: z.enum(['TODAY']).optional(),
    from: manilaDateSchema.optional(),
    to: manilaDateSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).max(10_000).default(0),
  })
  .superRefine(accountabilityDateRules);
export type ReconciliationListQuery = z.infer<typeof reconciliationListQuerySchema>;

/** GET /api/reconciliations/:id - detail (branch view filter only). */
export const reconciliationDetailQuerySchema = z.object({
  branch: z.string().trim().min(1).optional(),
});
export type ReconciliationDetailQuery = z.infer<typeof reconciliationDetailQuerySchema>;

/**
 * POST /api/reconciliations - create a DRAFT for one branch + business
 * day. The branch and business date are immutable after creation; actual
 * figures may be partial (null = not counted yet). Recorded totals are
 * NEVER accepted from the client - the server derives them.
 */
export const createReconciliationSchema = z.object({
  branch: z.string().trim().min(1, 'Branch is required'),
  businessDate: manilaDateSchema,
  ...actualsFields,
  notes: z.string().trim().max(500).optional(),
  evidenceNote: z.string().trim().max(500).optional(),
});
export type CreateReconciliationInput = z.infer<typeof createReconciliationSchema>;

/**
 * PATCH /api/reconciliations/:id - draft corrections only (409 once
 * submitted). Omitted fields stay unchanged; an explicit null clears an
 * actual figure back to "not counted".
 */
export const updateReconciliationSchema = z.object({
  ...actualsFields,
  notes: z.string().trim().max(500).nullable().optional(),
  evidenceNote: z.string().trim().max(500).nullable().optional(),
});
export type UpdateReconciliationInput = z.infer<typeof updateReconciliationSchema>;

/** POST /api/reconciliations/:id/review - reviewer note (optional). */
export const reviewReconciliationSchema = z.object({
  reviewNotes: z.string().trim().max(500).optional(),
});
export type ReviewReconciliationInput = z.infer<typeof reviewReconciliationSchema>;

/* ============================================================
 * Phase 2G.8 - daily operations & shift handover accountability
 * ============================================================ */

/**
 * GET /api/handovers query - admin-only operational handover list.
 * `branch` is a VIEW filter via resolveBranchScope; `status` narrows the
 * lifecycle state; the Manila `date`/`from`/`to` fields filter the
 * BUSINESS DATE the handover concerns; `q` matches the handover
 * reference (or a branch code) as a case-insensitive substring.
 */
export const handoverListQuerySchema = z
  .object({
    branch: z.string().trim().min(1).optional(),
    status: z.enum(HANDOVER_STATUSES).optional(),
    q: z.string().trim().min(1).max(64).optional(),
    date: z.enum(['TODAY']).optional(),
    from: manilaDateSchema.optional(),
    to: manilaDateSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).max(10_000).default(0),
  })
  .superRefine(accountabilityDateRules);
export type HandoverListQuery = z.infer<typeof handoverListQuerySchema>;

/** GET /api/handovers/:id - detail (branch view filter only). */
export const handoverDetailQuerySchema = z.object({
  branch: z.string().trim().min(1).optional(),
});
export type HandoverDetailQuery = z.infer<typeof handoverDetailQuerySchema>;

/**
 * POST /api/handovers - create a DRAFT. Branch and business date are
 * immutable after creation; the included transactions are derived
 * server-side at submission (no client can pick or exclude rows).
 */
export const createHandoverSchema = z.object({
  branch: z.string().trim().min(1, 'Branch is required'),
  businessDate: manilaDateSchema,
  notes: z.string().trim().max(500).optional(),
});
export type CreateHandoverInput = z.infer<typeof createHandoverSchema>;

/** PATCH /api/handovers/:id - draft notes correction only (409 once submitted). */
export const updateHandoverSchema = z.object({
  notes: z.string().trim().max(500).nullable().optional(),
});
export type UpdateHandoverInput = z.infer<typeof updateHandoverSchema>;

/** POST /api/handovers/:id/accept - recipient's note about unresolved items. */
export const acceptHandoverSchema = z.object({
  acceptanceNotes: z.string().trim().max(500).optional(),
});
export type AcceptHandoverInput = z.infer<typeof acceptHandoverSchema>;

/** Runtime shape of the stored actual figures (shared helpers operate on it). */
export type ReconciliationActualsInput = ReconciliationActuals;
export const ACTUAL_FIELDS = RECONCILIATION_ACTUAL_FIELDS;

/* ============================================================
 * Phase 2G.9 - operational loss prevention & control review
 * ============================================================ */

/**
 * Source-record selectors, one per supported module. Every selector is a
 * plain human reference the admin already sees on the source page - the
 * server resolves it against the scoped branch and rejects unknown or
 * cross-branch references exactly like every other module.
 */
export const controlReviewSourceSchema = z.discriminatedUnion('module', [
  z.object({
    module: z.literal('TRANSACTION'),
    /** Transaction id or branch number (LH1-000042). */
    ref: z.string().trim().min(1).max(64),
  }),
  z.object({
    module: z.literal('PROCESSING'),
    /** Processing-step id, or a transaction reference whose step must be named. */
    stepId: idSchema,
  }),
  z.object({
    module: z.literal('RECONCILIATION'),
    /** Reconciliation id. */
    id: idSchema,
  }),
  z.object({
    module: z.literal('INVENTORY'),
    /** Inventory item id. */
    id: idSchema,
  }),
  z.object({
    module: z.literal('PURCHASE'),
    /** Purchase id or number (PUR-LH1-000001). */
    ref: z.string().trim().min(1).max(64),
  }),
  z.object({
    module: z.literal('EXPENSE'),
    /** Expense id or number (EXP-LH1-000001). */
    ref: z.string().trim().min(1).max(64),
  }),
  z.object({
    module: z.literal('HANDOVER'),
    /** Handover id or reference (HOF-LH1-000001). */
    ref: z.string().trim().min(1).max(64),
  }),
]);
export type ControlReviewSourceInput = z.infer<typeof controlReviewSourceSchema>;

/**
 * POST /api/control-reviews - open a case against a source record.
 * Category + source pairing is validated server-side against
 * CONTROL_REVIEW_CATEGORY_SOURCES; the branch, priority, evidence
 * snapshot, reference and actor are ALWAYS server-derived - a client
 * cannot supply them (extra keys are stripped by zod).
 */
export const openControlReviewSchema = z
  .object({
    category: z.enum(CONTROL_REVIEW_CATEGORIES),
    source: controlReviewSourceSchema,
    /** Short factual title written by the reviewer (never an accusation). */
    title: z.string().trim().min(3, 'Title must be at least 3 characters').max(160),
    /** Optional factual context recorded with the case. */
    description: z.string().trim().max(2000).optional(),
    /** Optional initial investigation note. */
    initialNote: z.string().trim().max(2000).optional(),
  })
  .superRefine((val, ctx) => {
    const allowed = CONTROL_REVIEW_CATEGORY_SOURCES[val.category];
    if (!allowed.includes(val.source.module)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['source', 'module'],
        message: `Category ${val.category} cannot be opened against a ${val.source.module} record`,
      });
    }
  });
export type OpenControlReviewInput = z.infer<typeof openControlReviewSchema>;

/** GET /api/control-reviews query - list with filters (all re-validated server-side). */
export const controlReviewListQuerySchema = z
  .object({
    branch: z.string().trim().min(1).optional(),
    status: z.enum(CONTROL_REVIEW_STATUSES).optional(),
    category: z.enum(CONTROL_REVIEW_CATEGORIES).optional(),
    priority: z.enum(CONTROL_REVIEW_PRIORITIES).optional(),
    /** Manila date range over when the case was OPENED (createdAt). */
    date: z.enum(['TODAY']).optional(),
    from: manilaDateSchema.optional(),
    to: manilaDateSchema.optional(),
    /** Case reference (CRV-...) or source transaction reference substring. */
    q: z.string().trim().min(1).max(64).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).max(10_000).default(0),
  })
  .superRefine(accountabilityDateRules);
export type ControlReviewListQuery = z.infer<typeof controlReviewListQuerySchema>;

/** GET /api/control-reviews/:id - detail (branch view filter only). */
export const controlReviewDetailQuerySchema = z.object({
  branch: z.string().trim().min(1).optional(),
});
export type ControlReviewDetailQuery = z.infer<typeof controlReviewDetailQuerySchema>;

/** Assign a reviewer (an existing, active user id - resolved server-side). */
export const assignControlReviewSchema = z.object({
  reviewerId: idSchema,
});
export type AssignControlReviewInput = z.infer<typeof assignControlReviewSchema>;

/** Record a factual investigation note (never a verdict). */
export const controlReviewNoteSchema = z.object({
  note: z.string().trim().min(1, 'Note is required').max(2000),
});
export type ControlReviewNoteInput = z.infer<typeof controlReviewNoteSchema>;

/** Record the next corrective action (moves the case to ACTION_REQUIRED). */
export const controlReviewActionSchema = z.object({
  correctiveAction: z.string().trim().min(1, 'Corrective action is required').max(2000),
});
export type ControlReviewActionInput = z.infer<typeof controlReviewActionSchema>;

/** Resolve a case - a written explanation is always required. */
export const resolveControlReviewSchema = z.object({
  resolutionNotes: z.string().trim().min(1, 'Resolution notes are required').max(4000),
});
export type ResolveControlReviewInput = z.infer<typeof resolveControlReviewSchema>;

/** Reopen a resolved case - a reason is required; prior history is kept. */
export const reopenControlReviewSchema = z.object({
  reopenReason: z.string().trim().min(1, 'Reopen reason is required').max(2000),
});
export type ReopenControlReviewInput = z.infer<typeof reopenControlReviewSchema>;
